import { select, take, takeLatest } from "redux-saga/effects";
import { configuration } from "../../../api/client/SdsWebApiClient";
import { ImageSeries } from "../../../api/generated";
import Constants from "../../../constants";
import { ExternalMessages } from "../../../externalMessages";
import { ItemStates } from "../../../models/itemStates";
import { SummaryItem } from "../../../models/summaryItem";
import GaReporter from "../../../utilities/gaReporter";
import { ABORT_CONFIGURATION, CALL_TO_ACTION } from "../../actions/externalActions";
import {
    getAllImageSeries,
    getConfigurationString,
    getDealerAccount,
    getItemStates,
    getLanguage,
    getShowPrice,
    getSummaryItems,
    getTotalPrice,
} from "../selectors";

export function* callToActionSaga(): any {
    yield takeLatest(CALL_TO_ACTION, function* (action: any) {
        const summaryItems: SummaryItem[] = yield select(getSummaryItems);
        const configString: string = yield select(getConfigurationString);
        const dealer: string = yield select(getDealerAccount);
        const language: string = yield select(getLanguage);
        const itemStates: ItemStates = yield select(getItemStates);
        const showPrice: boolean = yield select(getShowPrice);
        const totalPrice: number = yield select(getTotalPrice);

        const makeImageUrl = (series: string, frame: number) => {
            const basePath = configuration.basePath.replace(/\/+$/, "");
            const imageUrl = new URL(`${basePath}/get_image_from_encoded_string`);
            imageUrl.search = "";
            imageUrl.searchParams.append("dealer_id", dealer);
            imageUrl.searchParams.append("serie", series);
            imageUrl.searchParams.append("frame", frame.toString());
            imageUrl.searchParams.append("encoded_config", configString);
            return imageUrl.toString();
        };

        const imageUrls: { [key: string]: string } = {};
        const imageSeries: ImageSeries[] = yield select(getAllImageSeries);
        imageSeries.forEach(s => {
            if (s.id === "EXTERIOR") {
                if (s.frames.includes(1)) imageUrls.frontRight = makeImageUrl(s.id, 1);
                if (s.frames.includes(2)) imageUrls.sideRight = makeImageUrl(s.id, 2);
                if (s.frames.includes(3)) imageUrls.rearRight = makeImageUrl(s.id, 3);
            } else if (s.id === "INTERIOR") {
                if (s.frames.includes(3)) imageUrls.luggage = makeImageUrl(s.id, 3);
            }
        });

        const url = new URL(window.location.href);
        const objectId = url.searchParams.get("object_id");
        const startConfig = url.searchParams.get("start_config");
        url.search = "";
        url.hash = "";
        url.searchParams.append(Constants.languageUrlQueryParam, language);
        url.searchParams.append(Constants.dealerAccountUrlQueryParam, dealer);

        if (startConfig) {
            url.searchParams.append("start_config", startConfig);
        } else {
            url.searchParams.append("config_string", configString);
        }

        if (summaryItems.length > 0) {
            const accessyIds = summaryItems.map(s => s.item.retailerId || s.item.id);
            url.searchParams.append("accessories", accessyIds.join(","));
        }

        if (objectId) {
            url.searchParams.append("object_id", objectId);
        }

        if (action.action === ExternalMessages.SUMMARY_CONTINUE) {
            GaReporter.trackProductCheckout(summaryItems, itemStates, showPrice, totalPrice);
            GaReporter.updateTransactionId();
        }

        try {
            const message = {
                type: action.action,
                summary: summaryItems.map(s => ({
                    displayText: s.summaryText,
                    retailerId: s.item.retailerId,
                    amount: s.amount,
                    price: itemStates.getPrice(s.item.id)?.priceValue || undefined,
                })),
                imageUrls,
                objectId,
                returnUrl: url.toString(),
            };

            window.parent.postMessage(message, "*");
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("CTA error", e);
        }
    });
}

export function* abortConfigurationSaga(): any {
    while (yield take(ABORT_CONFIGURATION)) {
        window.parent.postMessage({ type: ExternalMessages.ABORT_CONFIGURATION }, "*");
    }
}
